/**
 * FAQ Section - Interactive Accordion System
 * Adds smooth accordion functionality, keyboard navigation, and analytics
 */

class FAQSection {
  constructor() {
    this.section = document.querySelector('.faq-section');
    if (!this.section) {
      console.log('FAQ section not found');
      return;
    }
    
    this.faqItems = this.section.querySelectorAll('.faq-item');
    this.faqQuestions = this.section.querySelectorAll('.faq-question');
    this.faqAnswers = this.section.querySelectorAll('.faq-answer');
    
    console.log('FAQ elements found:', {
      items: this.faqItems.length,
      questions: this.faqQuestions.length,
      answers: this.faqAnswers.length
    });
    
    this.activeItem = null;
    this.animationDuration = 400;
    
    this.init();
  }

  init() {
    this.setupAccordion();
    this.setupKeyboardNavigation();
    this.setupAnalytics();
    this.setupAccessibility();
    this.preloadAnimations();
  }

  // Main accordion functionality
  setupAccordion() {
    this.faqQuestions.forEach((question, index) => {
      const faqItem = question.closest('.faq-item');
      const faqAnswer = faqItem.querySelector('.faq-answer');
      
      if (!faqItem || !faqAnswer) return;

      // Click handler
      question.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('FAQ clicked:', index); // Debug log
        this.toggleFAQ(faqItem, faqAnswer, question);
      });

      // Set initial ARIA attributes
      question.setAttribute('id', `faq-question-${index}`);
      faqAnswer.setAttribute('aria-labelledby', `faq-question-${index}`);
    });
    
    console.log('FAQ accordion setup complete. Found', this.faqQuestions.length, 'questions'); // Debug log
  }

  // Toggle FAQ item open/closed
  toggleFAQ(faqItem, faqAnswer, question) {
    const isActive = faqItem.classList.contains('faq-item--active');
    
    if (isActive) {
      this.closeFAQ(faqItem, faqAnswer, question);
    } else {
      // Close other open FAQs (optional - remove for multiple open)
      this.closeAllFAQs();
      this.openFAQ(faqItem, faqAnswer, question);
    }

    // Track analytics
    this.trackFAQInteraction(question, !isActive);
  }

  // Open FAQ item
  openFAQ(faqItem, faqAnswer, question) {
    // Update classes
    faqItem.classList.add('faq-item--active');
    faqAnswer.classList.add('faq-answer--open');
    
    // Update ARIA attributes
    question.setAttribute('aria-expanded', 'true');
    faqAnswer.setAttribute('aria-hidden', 'false');
    
    // Calculate and set height for smooth animation
    const contentHeight = faqAnswer.scrollHeight;
    faqAnswer.style.maxHeight = `${contentHeight}px`;
    
    // Store active item
    this.activeItem = faqItem;
    
    // Scroll into view if needed
    this.scrollToFAQIfNeeded(faqItem);
  }

  // Close FAQ item
  closeFAQ(faqItem, faqAnswer, question) {
    // Update classes
    faqItem.classList.remove('faq-item--active');
    faqAnswer.classList.remove('faq-answer--open');
    
    // Update ARIA attributes
    question.setAttribute('aria-expanded', 'false');
    faqAnswer.setAttribute('aria-hidden', 'true');
    
    // Animate height to 0
    faqAnswer.style.maxHeight = '0px';
    
    // Clear active item if this was it
    if (this.activeItem === faqItem) {
      this.activeItem = null;
    }
  }

  // Close all FAQ items
  closeAllFAQs() {
    this.faqItems.forEach(faqItem => {
      const question = faqItem.querySelector('.faq-question');
      const answer = faqItem.querySelector('.faq-answer');
      
      if (faqItem.classList.contains('faq-item--active')) {
        this.closeFAQ(faqItem, answer, question);
      }
    });
  }

  // Scroll to FAQ if it's not fully visible
  scrollToFAQIfNeeded(faqItem) {
    const rect = faqItem.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // If FAQ is not fully visible, scroll to it
    if (rect.bottom > windowHeight - 100) {
      setTimeout(() => {
        faqItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, this.animationDuration / 2);
    }
  }

  // Keyboard navigation
  setupKeyboardNavigation() {
    this.faqQuestions.forEach((question, index) => {
      question.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            question.click();
            break;
            
          case 'ArrowDown':
            e.preventDefault();
            this.focusNextQuestion(index);
            break;
            
          case 'ArrowUp':
            e.preventDefault();
            this.focusPrevQuestion(index);
            break;
            
          case 'Home':
            e.preventDefault();
            this.faqQuestions[0].focus();
            break;
            
          case 'End':
            e.preventDefault();
            this.faqQuestions[this.faqQuestions.length - 1].focus();
            break;
            
          case 'Escape':
            e.preventDefault();
            this.closeAllFAQs();
            break;
        }
      });
    });
  }

  // Focus next question
  focusNextQuestion(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.faqQuestions.length;
    this.faqQuestions[nextIndex].focus();
  }

  // Focus previous question
  focusPrevQuestion(currentIndex) {
    const prevIndex = currentIndex === 0 ? this.faqQuestions.length - 1 : currentIndex - 1;
    this.faqQuestions[prevIndex].focus();
  }

  // Analytics tracking
  setupAnalytics() {
    // Track FAQ section view
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.trackFAQSectionView();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });

      observer.observe(this.section);
    }
  }

  // Track FAQ interaction
  trackFAQInteraction(question, isOpening) {
    const questionText = question.querySelector('.faq-question-text').textContent;
    
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'faq_interaction', {
        event_category: 'FAQ',
        event_label: questionText,
        action: isOpening ? 'open' : 'close'
      });
    }

    // Shopify Analytics
    if (typeof analytics !== 'undefined') {
      analytics.track('FAQ Interaction', {
        question: questionText,
        action: isOpening ? 'open' : 'close',
        section: 'FAQ'
      });
    }
  }

  // Track FAQ section view
  trackFAQSectionView() {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'view_faq_section', {
        event_category: 'Engagement',
        event_label: 'FAQ Section'
      });
    }

    // Shopify Analytics
    if (typeof analytics !== 'undefined') {
      analytics.track('FAQ Section Viewed', {
        section: 'FAQ'
      });
    }
  }

  // Accessibility enhancements
  setupAccessibility() {
    // Add role and aria-label to accordion
    const accordion = this.section.querySelector('.faq-accordion');
    if (accordion) {
      accordion.setAttribute('role', 'region');
      accordion.setAttribute('aria-label', 'Frequently Asked Questions');
    }

    // Enhance focus management
    this.faqQuestions.forEach(question => {
      question.addEventListener('focus', () => {
        // Ensure focused item is visible
        question.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      });
    });
  }

  // Preload and setup initial states
  preloadAnimations() {
    // Set initial max-height for all answers
    this.faqAnswers.forEach(answer => {
      answer.style.maxHeight = '0px';
    });

    // Add smooth transition after initial setup
    setTimeout(() => {
      this.faqAnswers.forEach(answer => {
        answer.style.transition = `max-height ${this.animationDuration}ms ease`;
      });
    }, 100);
  }

  // Public methods for external control
  openFAQByIndex(index) {
    const faqItem = this.faqItems[index];
    if (faqItem && !faqItem.classList.contains('faq-item--active')) {
      const question = faqItem.querySelector('.faq-question');
      const answer = faqItem.querySelector('.faq-answer');
      this.openFAQ(faqItem, answer, question);
    }
  }

  closeFAQByIndex(index) {
    const faqItem = this.faqItems[index];
    if (faqItem && faqItem.classList.contains('faq-item--active')) {
      const question = faqItem.querySelector('.faq-question');
      const answer = faqItem.querySelector('.faq-answer');
      this.closeFAQ(faqItem, answer, question);
    }
  }

  // Search FAQ content
  searchFAQs(searchTerm) {
    const results = [];
    
    this.faqItems.forEach((item, index) => {
      const question = item.querySelector('.faq-question-text').textContent.toLowerCase();
      const answer = item.querySelector('.faq-answer-text').textContent.toLowerCase();
      const term = searchTerm.toLowerCase();
      
      if (question.includes(term) || answer.includes(term)) {
        results.push({
          index,
          question: item.querySelector('.faq-question-text').textContent,
          answer: item.querySelector('.faq-answer-text').textContent
        });
      }
    });
    
    return results;
  }
}

// Add CSS for enhanced animations
const style = document.createElement('style');
style.textContent = `
  .faq-question:active {
    transform: scale(0.98);
  }
  
  .faq-item--active .faq-question {
    background: rgba(5, 150, 105, 0.03);
  }
  
  .faq-answer-content {
    transform: translateY(-10px);
    opacity: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
  }
  
  .faq-answer--open .faq-answer-content {
    transform: translateY(0);
    opacity: 1;
    transition-delay: 0.1s;
  }
  
  @media (prefers-reduced-motion: reduce) {
    .faq-answer-content {
      transform: none !important;
      opacity: 1 !important;
      transition: none !important;
    }
  }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FAQSection());
} else {
  new FAQSection();
}

// Re-initialize on Shopify section reload
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('faq')) {
    new FAQSection();
  }
});

// Export for potential external use
window.FAQSection = FAQSection;
